import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminDashboard.css";
import { adminFetch, ADMIN_PASSWORD_STORAGE_KEY } from "./utils/adminApi";
import AdminPostManagement from "./AdminPostManagement";
import AdminUserManagement from "./AdminUserManagement";
import AdminErrorManagement from "./AdminErrorManagement";
import AdminTransactionManagement from "./AdminTransactionManagement";
import AdminUserPostManagement from "./AdminUserPostManagement";
import AdminNotificationScreen from "./AdminNotificationScreen";
import AdminNewsManagement from "./AdminNewsManagement";
import AdminPostHistoryManagement from "./AdminPostHistoryManagement";
import AdminCommentManagement from "./AdminCommentManagement";
import AdminAffiliateManagement from "./AdminAffiliateManagement";
import AdminWithdrawalsScreen from "./AdminWithdrawalsScreen";
import GroundingUsageWidget from "./components/GroundingUsageWidget";

// AdminLogin Component - Senior FE: Separate login logic for better maintainability
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg("");

    try {
      const normalizedPassword = password.trim();
      if (!normalizedPassword) {
        setErrorMsg("Vui lòng nhập mật khẩu admin.");
        return;
      }

      await adminFetch("/api/admin/stats", {
        headers: {
          "X-Admin-Password": normalizedPassword,
        },
      });

      if (normalizedPassword) {
        localStorage.setItem("adminAuthenticated", "true");
        localStorage.setItem(ADMIN_PASSWORD_STORAGE_KEY, normalizedPassword);
        onLogin();
      } else {
        setErrorMsg("Mật khẩu không đúng");
      }
    } catch (error) {
      setErrorMsg(error?.message || "Mật khẩu không đúng hoặc đã hết hạn.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-login" role="main" aria-labelledby="login-title">
      <div className="admin-login-container">
        <div className="admin-login-card">
          <h2 id="login-title" className="admin-login-title">
            Đăng nhập Admin
          </h2>
          <p className="admin-login-subtitle">
            Vui lòng nhập mật khẩu để truy cập trang quản trị
          </p>

          <form onSubmit={handleSubmit} className="admin-login-form" noValidate>
            <div className="form-group">
              <label htmlFor="admin-password" className="form-label">
                Mật khẩu Admin
              </label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Nhập mật khẩu"
                className="form-input"
                required
                autoComplete="current-password"
                aria-describedby={errorMsg ? "password-error" : undefined}
                disabled={isLoading}
              />
              {errorMsg && (
                <div id="password-error" className="error-message" role="alert">
                  {errorMsg}
                </div>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-login"
              disabled={isLoading || !password.trim()}
            >
              {isLoading ? (
                <>
                  <div className="spinner spinner-sm"></div>
                  Đang đăng nhập...
                </>
              ) : (
                "Đăng nhập"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// DashboardStats Component - Senior FE: Separate stats for better reusability
const DashboardStats = ({ stats, formatCurrency }) => {
  const statItems = useMemo(
    () => [
      {
        id: "totalUsers",
        title: "Tổng số người dùng",
        value: stats.totalUsers?.toLocaleString("vi-VN") || "0",
        icon: "👥",
        color: "blue",
      },
      {
        id: "activeUsers",
        title: "Người dùng hoạt động",
        value: stats.activeUsers?.toLocaleString("vi-VN") || "0",
        icon: "✅",
        color: "green",
      },
      {
        id: "totalTransactions",
        title: "Tổng số giao dịch",
        value: stats.totalTransactions?.toLocaleString("vi-VN") || "0",
        icon: "💳",
        color: "purple",
      },
      {
        id: "totalRevenue",
        title: "Tổng doanh thu",
        value: formatCurrency(stats.totalRevenue || 0),
        icon: "💰",
        color: "orange",
      },
    ],
    [stats, formatCurrency]
  );

  return (
    <div className="stats-grid">
      {statItems.map((stat) => (
        <div key={stat.id} className={`stat-card stat-card-${stat.color}`}>
          <div className="stat-card-header">
            <div className="stat-icon">{stat.icon}</div>
            <p className="stat-card-title">{stat.title}</p>
            <p className="stat-card-value">{stat.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// RecentUsersTable Component - Senior FE: Separate table component
const RecentUsersTable = ({ users, formatDate, onViewAll }) => {
  if (!users?.length) {
    return (
      <div className="data-table">
        <div className="table-header">
          <h3 className="table-title">Người dùng gần đây</h3>
        </div>
        <div className="table-content">
          <div className="empty-state">
            <div className="empty-state-icon">👤</div>
            <p className="empty-state-text">Chưa có người dùng nào</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="data-table">
      <div className="table-header">
        <h3 className="table-title">Người dùng gần đây</h3>
      </div>
      <div className="table-content">
        <div className="overflow-x-auto">
          <table role="table" aria-label="Người dùng gần đây">
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Tên</th>
                <th scope="col">Email</th>
                <th scope="col">Trạng thái</th>
                <th scope="col">Ngày tham gia</th>
              </tr>
            </thead>
            <tbody>
              {users.slice(0, 5).map((user) => (
                <tr key={user.id}>
                  <td>
                    <code className="user-id" title={user.id}>
                      {user.id.substring(0, 8)}...
                    </code>
                  </td>
                  <td>{user.displayName || "N/A"}</td>
                  <td>
                    <a href={`mailto:${user.email}`} className="email-link">
                      {user.email || "N/A"}
                    </a>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        user.status === "active"
                          ? "badge-success"
                          : "badge-warning"
                      }`}
                    >
                      {user.status === "active"
                        ? "Hoạt động"
                        : "Không hoạt động"}
                    </span>
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 text-right">
          <button
            onClick={onViewAll}
            className="btn btn-primary"
            aria-label="Xem tất cả người dùng"
          >
            Xem tất cả
          </button>
        </div>
      </div>
    </div>
  );
};

// RecentTransactionsTable Component - Senior FE: Separate table component
const RecentTransactionsTable = ({
  transactions,
  formatCurrency,
  formatDate,
  onViewAll,
}) => {
  if (!transactions?.length) {
    return (
      <div className="data-table">
        <div className="table-header">
          <h3 className="table-title">Giao dịch gần đây</h3>
        </div>
        <div className="table-content">
          <div className="empty-state">
            <div className="empty-state-icon">💳</div>
            <p className="empty-state-text">Chưa có giao dịch nào</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="data-table">
      <div className="table-header">
        <h3 className="table-title">Giao dịch gần đây</h3>
      </div>
      <div className="table-content">
        <div className="overflow-x-auto">
          <table role="table" aria-label="Giao dịch gần đây">
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Người dùng</th>
                <th scope="col">Tên gói</th>
                <th scope="col">Số tiền</th>
                <th scope="col">Mã tham chiếu</th>
                <th scope="col">Trạng thái</th>
                <th scope="col">Ngày</th>
              </tr>
            </thead>
            <tbody>
              {transactions.slice(0, 5).map((transaction) => (
                <tr key={transaction.id}>
                  <td>
                    <code className="user-id" title={transaction.id}>
                      {transaction.id.substring(0, 8)}...
                    </code>
                  </td>
                  <td>
                    <code className="user-id" title={transaction.userId}>
                      {transaction.userId?.substring(0, 8) || "N/A"}
                    </code>
                  </td>
                  <td>{transaction.itemName || "N/A"}</td>
                  <td className="text-right font-medium">
                    {formatCurrency(transaction.amount)}
                  </td>
                  <td>
                    <code className="reference-code">
                      {transaction.paymentInfo?.reference || "N/A"}
                    </code>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        transaction.status === "completed" ||
                        transaction.status === "PAID"
                          ? "badge-success"
                          : "badge-warning"
                      }`}
                    >
                      {transaction.status === "completed" ||
                      transaction.status === "PAID"
                        ? "Hoàn thành"
                        : "Đang xử lý"}
                    </span>
                  </td>
                  <td>{formatDate(transaction.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 text-right">
          <button
            onClick={onViewAll}
            className="btn btn-primary"
            aria-label="Xem tất cả giao dịch"
          >
            Xem tất cả
          </button>
        </div>
      </div>
    </div>
  );
};

// SystemSettings Component - Senior FE: Separate settings component
const SystemSettings = ({ maintenanceMode, onToggleMaintenance }) => {
  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span className="text-xl">⚙️</span>
          Chế độ bảo trì
        </h3>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex-1">
            <p className="text-gray-600 mb-2">
              Khi bật chế độ bảo trì, người dùng chỉ có thể truy cập trang chủ
              và trang admin. Tất cả các tính năng khác sẽ bị khóa.
            </p>
            <p
              className={`text-sm font-medium ${
                maintenanceMode ? "text-red-600" : "text-green-600"
              }`}
            >
              Trạng thái hiện tại:{" "}
              {maintenanceMode ? "🔴 Đang bảo trì" : "🟢 Hoạt động bình thường"}
            </p>
          </div>
          <div className="flex-shrink-0">
            <button
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                maintenanceMode
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-red-600 hover:bg-red-700 text-white"
              } disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
              onClick={onToggleMaintenance}
              aria-pressed={maintenanceMode}
              aria-label={
                maintenanceMode ? "Tắt chế độ bảo trì" : "Bật chế độ bảo trì"
              }
            >
              {maintenanceMode ? "🟢 Tắt bảo trì" : "🔴 Bật bảo trì"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();

  // State management - Senior FE: Better organization
  const [authState, setAuthState] = useState(() => ({
    isAuthenticated: localStorage.getItem("adminAuthenticated") === "true",
    isLoading: false,
    error: null,
  }));

  const [dashboardData, setDashboardData] = useState({
    users: [],
    transactions: [],
    stats: {
      totalUsers: 0,
      activeUsers: 0,
      totalTransactions: 0,
      totalRevenue: 0,
    },
    maintenanceMode: false,
    loading: true,
    error: null,
  });

  const [uiState, setUiState] = useState({
    activeSection: "dashboard",
    menuCollapsed: (() => {
      const savedPreference = localStorage.getItem("sidebarCollapsed");
      return savedPreference ? JSON.parse(savedPreference) : false;
    })(),
  });

  useEffect(() => {
    const storedAdminPassword = localStorage.getItem(ADMIN_PASSWORD_STORAGE_KEY);
    const fallbackAdminPassword =
      process.env.REACT_APP_ADMIN_PASSWORD || process.env.ADMIN_PASSWORD;

    if (
      authState.isAuthenticated &&
      !storedAdminPassword &&
      !fallbackAdminPassword
    ) {
      localStorage.removeItem("adminAuthenticated");
      setAuthState((prev) => ({ ...prev, isAuthenticated: false }));
    }
  }, [authState.isAuthenticated]);

  // Menu handlers - Senior FE: Use useCallback for performance
  const toggleMenu = useCallback(() => {
    setUiState((prev) => {
      const newState = !prev.menuCollapsed;
      localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
      return { ...prev, menuCollapsed: newState };
    });
  }, []);

  const setActiveSection = useCallback((section) => {
    setUiState((prev) => ({ ...prev, activeSection: section }));
  }, []);

  // Authentication handler
  const handleLogin = useCallback(() => {
    setAuthState((prev) => ({ ...prev, isAuthenticated: true }));
  }, []);

  // Utility functions - Senior FE: Memoized for performance
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  }, []);

  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }, []);

  // Maintenance mode handler
  const handleToggleMaintenance = useCallback(async () => {
    try {
      const newMode = !dashboardData.maintenanceMode;
      const result = await adminFetch("/api/admin/system/maintenance", {
        method: "PATCH",
        body: JSON.stringify({ maintenanceMode: newMode }),
      });
      setDashboardData((prev) => ({
        ...prev,
        maintenanceMode: result.data?.maintenanceMode ?? newMode,
      }));
    } catch (error) {
      console.error("Error updating maintenance mode:", error);
      setDashboardData((prev) => ({
        ...prev,
        error: "Có lỗi khi cập nhật trạng thái bảo trì",
      }));
    }
  }, [dashboardData.maintenanceMode]);

  // Navigation handlers
  const handleViewAllUsers = useCallback(() => {
    setActiveSection("users");
  }, [setActiveSection]);

  const handleViewAllTransactions = useCallback(() => {
    setActiveSection("transactions");
  }, [setActiveSection]);

  // Render content based on active section - Senior FE: Memoized
  const renderContent = useCallback(() => {
    switch (uiState.activeSection) {
      case "users":
        return <AdminUserManagement />;
      case "posts":
        return <AdminPostManagement />;
      case "user-posts":
        return <AdminUserPostManagement />;
      case "post-history":
        return <AdminPostHistoryManagement />;
      case "transactions":
        return <AdminTransactionManagement />;
      case "affiliates":
        return <AdminAffiliateManagement />;
      case "withdrawals":
        return <AdminWithdrawalsScreen />;
      case "errors":
        return <AdminErrorManagement />;
      case "notifications":
        return <AdminNotificationScreen />;
      case "news":
        return <AdminNewsManagement />;
      case "comment-management":
        return <AdminCommentManagement />;
      case "settings":
        return (
          <SystemSettings
            maintenanceMode={dashboardData.maintenanceMode}
            onToggleMaintenance={handleToggleMaintenance}
          />
        );
      default:
        return (
          <>
            <div className="content-header">
              <h2 className="text-2xl font-semibold text-gray-800">
                Tổng quan Admin
              </h2>
            </div>

            <GroundingUsageWidget />

            <DashboardStats
              stats={dashboardData.stats}
              formatCurrency={formatCurrency}
            />

            <RecentUsersTable
              users={dashboardData.users}
              formatDate={formatDate}
              onViewAll={handleViewAllUsers}
            />

            <RecentTransactionsTable
              transactions={dashboardData.transactions}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              onViewAll={handleViewAllTransactions}
            />

            <SystemSettings
              maintenanceMode={dashboardData.maintenanceMode}
              onToggleMaintenance={handleToggleMaintenance}
            />

            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <span className="text-xl">🚀</span>
                Chức năng Admin khác
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  {
                    key: "posts",
                    icon: "📝",
                    title: "Quản lý bài viết",
                    desc: "Xem và quản lý tất cả bài viết",
                  },
                  {
                    key: "user-posts",
                    icon: "👥",
                    title: "Bài viết người dùng",
                    desc: "Quản lý bài viết của người dùng",
                  },
                  {
                    key: "post-history",
                    icon: "📊",
                    title: "Lịch sử bài viết",
                    desc: "Xem lịch sử đăng bài",
                  },
                  {
                    key: "affiliates",
                    icon: "💸",
                    title: "Quản lý affiliate",
                    desc: "Theo dõi doanh số, hoa hồng và cấu hình %",
                  },
                  {
                    key: "withdrawals",
                    icon: "💳",
                    title: "Yêu cầu rút tiền",
                    desc: "Duyệt và quản lý rút tiền affiliate",
                  },
                  {
                    key: "errors",
                    icon: "⚠️",
                    title: "Lỗi hệ thống",
                    desc: "Xem và xử lý lỗi",
                  },
                  {
                    key: "notifications",
                    icon: "📢",
                    title: "Gửi thông báo",
                    desc: "Gửi thông báo đến người dùng",
                  },
                  {
                    key: "news",
                    icon: "📰",
                    title: "Quản lý tin tức",
                    desc: "Quản lý tin tức và bài viết",
                  },
                  {
                    key: "auto-settings", // This key will need handling in renderContent or onClick
                    icon: "⚙️",
                    title: "Cấu hình tự động",
                    desc: "Xem cài đặt tự động của user",
                  },
                  {
                    key: "comment-management",
                    icon: "💬",
                    title: "Lead Conversion Engine",
                    desc: "Quản trị engine, review và queue vận hành",
                  },
                ].map((item) => (
                  <button
                    key={item.key}
                    onClick={() => {
                      if (item.key === "auto-settings") {
                        navigate("/admin/auto-settings");
                      } else if (item.key === "comment-management") {
                        navigate("/admin/lead-engine");
                      } else {
                        setActiveSection(item.key);
                      }
                    }}
                    className="bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 p-4 rounded-lg transition-all duration-200 text-left border border-blue-200 hover:border-blue-300 hover:shadow-md"
                  >
                    <div className="text-2xl mb-2">{item.icon}</div>
                    <div className="text-blue-700 font-semibold text-sm mb-1">
                      {item.title}
                    </div>
                    <div className="text-blue-600 text-xs leading-relaxed">
                      {item.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        );
    }
  }, [
    uiState.activeSection,
    dashboardData,
    formatCurrency,
    formatDate,
    handleToggleMaintenance,
    handleViewAllUsers,
    handleViewAllTransactions,
    setActiveSection,
  ]);

  // Data fetching functions - Senior FE: Separated for better testability
  // OPTIMIZED: Use backend API instead of fetching all data from Firebase
  // This reduces bandwidth significantly by only receiving the necessary data
  const fetchDashboardData = useCallback(async () => {
    setDashboardData((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // Use optimized backend API endpoint
      const data = await adminFetch("/api/admin/stats");

      if (data.success) {
        setDashboardData((prev) => ({
          ...prev,
          users: data.recentUsers || [],
          transactions: data.recentTransactions || [],
          stats: data.stats || {
            totalUsers: 0,
            activeUsers: 0,
            totalTransactions: 0,
            totalRevenue: 0,
          },
          maintenanceMode: data.maintenanceMode || false,
          loading: false,
        }));
      } else {
        throw new Error(data.error || "Unknown error");
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setDashboardData((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Không thể tải dữ liệu dashboard",
      }));
    }
  }, []);

  // Effect for data fetching - Senior FE: Simplified and optimized
  useEffect(() => {
    if (authState.isAuthenticated) {
      fetchDashboardData();
    }
  }, [authState.isAuthenticated, fetchDashboardData]);

  // Effect for responsive menu handling
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setUiState((prev) => ({ ...prev, menuCollapsed: true }));
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Login screen - Senior FE: Improved accessibility and UX
  if (!authState.isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  // Loading state - Senior FE: Better UX with skeleton
  if (dashboardData.loading) {
    return (
      <div
        className={`admin-dashboard ${
          uiState.menuCollapsed ? "sidebar-collapsed" : ""
        } ${window.innerWidth <= 768 ? "mobile-view" : ""} ${
          window.innerWidth <= 768 && uiState.menuCollapsed
            ? "mobile-menu-collapsed"
            : ""
        }`}
      >
        <div className="admin-sidebar">
          <div className="sidebar-header">
            <h3 className="text-xl font-bold">Admin Dashboard</h3>
          </div>
        </div>
        <div className="main-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p className="mt-4 text-gray-600">Đang tải dữ liệu...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state - Senior FE: Better error UI with retry option
  if (dashboardData.error) {
    return (
      <div
        className={`admin-dashboard ${
          uiState.menuCollapsed ? "sidebar-collapsed" : ""
        } ${window.innerWidth <= 768 ? "mobile-view" : ""} ${
          window.innerWidth <= 768 && uiState.menuCollapsed
            ? "mobile-menu-collapsed"
            : ""
        }`}
      >
        <div className="admin-sidebar">
          <div className="sidebar-header">
            <h3 className="text-xl font-bold">Admin Dashboard</h3>
          </div>
        </div>
        <div className="main-content">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h3 className="error-title">Có lỗi xảy ra</h3>
            <p className="error-message">{dashboardData.error}</p>
            <button onClick={fetchDashboardData} className="btn btn-primary">
              Thử lại
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`admin-dashboard ${
        uiState.menuCollapsed ? "sidebar-collapsed" : ""
      } ${window.innerWidth <= 768 ? "mobile-view" : ""} ${
        window.innerWidth <= 768 && uiState.menuCollapsed
          ? "mobile-menu-collapsed"
          : ""
      }`}
    >
      {/* Sidebar */}
      <div className="admin-sidebar">
        <div className="sidebar-header flex justify-between items-center">
          <h3 className="text-xl font-bold">Admin Dashboard</h3>
          <button
            className="menu-toggle"
            onClick={toggleMenu}
            aria-label={uiState.menuCollapsed ? "Mở rộng menu" : "Thu gọn menu"}
            title={uiState.menuCollapsed ? "Mở rộng menu" : "Thu gọn menu"}
          >
            {uiState.menuCollapsed ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
          </button>
        </div>
        <div className="mt-4">
          {/* Quick Navigation Links */}
          <div className="mb-6">
            <div className="space-y-2">
              <button
                onClick={() => setActiveSection("dashboard")}
                className={`sidebar-item sidebar-item-tooltip block w-full text-left ${
                  uiState.activeSection === "dashboard" ? "active" : ""
                }`}
                data-tooltip="Tổng quan"
                aria-current={
                  uiState.activeSection === "dashboard" ? "page" : undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
                Tổng quan
              </button>
              <button
                onClick={() => setActiveSection("users")}
                className={`sidebar-item sidebar-item-tooltip block w-full text-left ${
                  uiState.activeSection === "users" ? "active" : ""
                }`}
                data-tooltip="Quản lý người dùng"
                aria-current={
                  uiState.activeSection === "users" ? "page" : undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
                Người dùng
              </button>
              <button
                onClick={() => setActiveSection("posts")}
                className={`sidebar-item sidebar-item-tooltip block w-full text-left ${
                  uiState.activeSection === "posts" ? "active" : ""
                }`}
                data-tooltip="Quản lý bài viết"
                aria-current={
                  uiState.activeSection === "posts" ? "page" : undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4V5h12v10z"
                    clipRule="evenodd"
                  />
                  <path d="M7 7h6v2H7V7zm0 4h6v2H7v-2z" />
                </svg>
                Quản lý bài viết
              </button>
              <button
                onClick={() => setActiveSection("user-posts")}
                className={`sidebar-item sidebar-item-tooltip block w-full text-left ${
                  uiState.activeSection === "user-posts" ? "active" : ""
                }`}
                data-tooltip="Bài viết người dùng"
                aria-current={
                  uiState.activeSection === "user-posts" ? "page" : undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                  <path d="M6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                </svg>
                Bài viết người dùng
              </button>
              <button
                onClick={() => setActiveSection("post-history")}
                className={`sidebar-item sidebar-item-tooltip block w-full text-left ${
                  uiState.activeSection === "post-history" ? "active" : ""
                }`}
                data-tooltip="Lịch sử bài viết"
                aria-current={
                  uiState.activeSection === "post-history" ? "page" : undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                    clipRule="evenodd"
                  />
                </svg>
                Lịch sử bài viết
              </button>
              <button
                onClick={() => setActiveSection("transactions")}
                className={`sidebar-item sidebar-item-tooltip block w-full text-left ${
                  uiState.activeSection === "transactions" ? "active" : ""
                }`}
                data-tooltip="Quản lý giao dịch"
                aria-current={
                  uiState.activeSection === "transactions" ? "page" : undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                  <path
                    fillRule="evenodd"
                    d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                    clipRule="evenodd"
                  />
                </svg>
                Giao dịch
              </button>
              <button
                onClick={() => setActiveSection("affiliates")}
                className={`sidebar-item sidebar-item-tooltip block w-full text-left ${
                  uiState.activeSection === "affiliates" ? "active" : ""
                }`}
                data-tooltip="Quản lý affiliate"
                aria-current={
                  uiState.activeSection === "affiliates" ? "page" : undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M10 2a1 1 0 011 1v1.05a4.5 4.5 0 012.856 1.38.75.75 0 11-1.06 1.06A3 3 0 0010.5 5.5h-1A2.5 2.5 0 007 8c0 1.103.897 2 2 2h2a3.5 3.5 0 110 7H11V18a1 1 0 11-2 0v-1.05a4.5 4.5 0 01-2.856-1.38.75.75 0 111.06-1.06A3 3 0 009.5 16.5h1A2.5 2.5 0 0013 14c0-1.103-.897-2-2-2H9a3.5 3.5 0 110-7h.5V3a1 1 0 011-1z" />
                </svg>
                Quản lý affiliate
              </button>
              <button
                onClick={() => setActiveSection("withdrawals")}
                className={`sidebar-item sidebar-item-tooltip block w-full text-left ${
                  uiState.activeSection === "withdrawals" ? "active" : ""
                }`}
                data-tooltip="Yêu cầu rút tiền"
                aria-current={
                  uiState.activeSection === "withdrawals" ? "page" : undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 3a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" />
                </svg>
                Yêu cầu rút tiền
              </button>
              <button
                onClick={() => setActiveSection("errors")}
                className={`sidebar-item sidebar-item-tooltip block w-full text-left ${
                  uiState.activeSection === "errors" ? "active" : ""
                }`}
                data-tooltip="Lỗi hệ thống"
                aria-current={
                  uiState.activeSection === "errors" ? "page" : undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Lỗi hệ thống
              </button>
              <button
                onClick={() => setActiveSection("notifications")}
                className={`sidebar-item sidebar-item-tooltip block w-full text-left ${
                  uiState.activeSection === "notifications" ? "active" : ""
                }`}
                data-tooltip="Gửi thông báo"
                aria-current={
                  uiState.activeSection === "notifications" ? "page" : undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
                Gửi thông báo
              </button>
              <button
                onClick={() => setActiveSection("news")}
                className={`sidebar-item sidebar-item-tooltip block w-full text-left ${
                  uiState.activeSection === "news" ? "active" : ""
                }`}
                data-tooltip="Quản lý tin tức"
                aria-current={
                  uiState.activeSection === "news" ? "page" : undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M2 5a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 002 2H4a2 2 0 01-2-2V5zm3 1h6v4H5V6zm6 6H5v2h6v-2z"
                    clipRule="evenodd"
                  />
                  <path d="M15 7h1a2 2 0 012 2v5.5a1.5 1.5 0 01-3 0V7z" />
                </svg>
                Quản lý tin tức
              </button>
              <button
                onClick={() => navigate("/admin/lead-engine")}
                className={`sidebar-item sidebar-item-tooltip block w-full text-left ${
                  uiState.activeSection === "comment-management" ? "active" : ""
                }`}
                data-tooltip="Lead Conversion Engine"
                aria-current={
                  uiState.activeSection === "comment-management" ? "page" : undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z"
                    clipRule="evenodd"
                  />
                </svg>
                Lead Conversion Engine
              </button>
              <button
                onClick={() => setActiveSection("settings")}
                className={`sidebar-item sidebar-item-tooltip block w-full text-left ${
                  uiState.activeSection === "settings" ? "active" : ""
                }`}
                data-tooltip="Quản lý cài đặt"
                aria-current={
                  uiState.activeSection === "settings" ? "page" : undefined
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                    clipRule="evenodd"
                  />
                </svg>
                Quản lý cài đặt
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">{renderContent()}</div>
    </div>
  );
};

export default AdminDashboard;
